import { v4 as uuidv4 } from "uuid"
import { IFilter } from '../models/Filter'
import { HttpRequest } from "../utils"

const httpRequest = new HttpRequest()

// §26 — backend wraps `/delivery_note/assign` in `withIdempotency` (non-strict:
// a missing key just skips dedup, never 400s). A fresh uuid v4 per call lets a
// genuine network retry replay safely instead of double-assigning.
const idem = (): Record<string, string> => ({ "Idempotency-Key": uuidv4() })

export const list = async ({ offset, filter, order }: IFilter) => {
  return httpRequest.post("/ord/delivery_note/list", { offset, filter, order })
}

export const assign = async (data: any) => {
  return httpRequest.put(`/ord/delivery_note/assign`, data, idem())
}

export const create = async (data: any) => {
  return httpRequest.post(`/ord/delivery_note`, data)
}

export const approve = async (data: any) => {
  return httpRequest.put(`/ord/delivery_note/approve`, data)
}

/** BE delivery-note/active-stats.ts — business-wide KPI counts for the Идэвхтэй tab (O105-O107). */
export interface IActiveDeliveryStats {
  active: number
  byStatus: { ASSIGNED: number; LOADED: number; PENDING_HANDOVER: number; DELIVERING: number }
  unassigned: number
  live: number
  lastGpsAt: string | null
  slaRisk: number
  vipUrgent: number
  deliveredToday: number
  definitions: { liveWindowMinutes: number; slaRiskHours: number; urgentHours: number; vipAmount: number }
}

export const activeStats = async (body: { orderType?: string | null } = {}): Promise<IActiveDeliveryStats> => {
  return httpRequest.post("/ord/delivery_note/active-stats", body)
}

export const staffSelect = async (data: any) => {
  return httpRequest.get("/ord/delivery_note/staff_select", data)
}

export const get = async (id: string) => {
  return httpRequest.get(`/ord/delivery_note/${id}`)
}

export const warehouseSelect = async (params: { businessId: string; query?: string | null; limit?: number }) => {
  return httpRequest.get("/ord/order/backorder/warehouse/select", params)
}

export const setSourceWarehouse = async (deliveryNoteId: string, newSourceWarehouseId: string | null) => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/source-warehouse`, { newSourceWarehouseId })
}

// Spec §2.5 detail-panel — DN audit log + free-text note.

export interface IDeliveryAuditLogEntry {
  id: string
  deliveryNoteId: string
  businessId: string
  actionType: string
  actorUserId: string | null
  context: Record<string, unknown>
  notes: string | null
  occurredAt: string
}

export interface IAuditLogResponse {
  rows: IDeliveryAuditLogEntry[]
  count: number
}

export const auditLog = async (
  deliveryNoteId: string,
  params: { limit?: number; skip?: number } = {},
): Promise<IAuditLogResponse> => {
  const query: Record<string, number> = {}
  if (params.limit != null) query.limit = params.limit
  if (params.skip != null) query.skip = params.skip
  return httpRequest.get(`/ord/delivery_management/${deliveryNoteId}/audit-log`, query)
}

export const addNote = async (
  deliveryNoteId: string,
  note: string,
): Promise<{ success: boolean; entry: IDeliveryAuditLogEntry | null }> => {
  return httpRequest.post(`/ord/delivery_management/${deliveryNoteId}/note`, { note })
}

export const postProgressNote = async (
  deliveryNoteId: string,
  note: string,
): Promise<{ success: boolean; entry: IDeliveryAuditLogEntry | null }> => {
  return httpRequest.post(`/ord/delivery_management/${deliveryNoteId}/progress-note`, { note })
}

// Spec §2.5 detail-panel — Reassign action.

export interface IReassignInput {
  toDriverId?: string | null
  toVehicleId?: string | null
  toScheduledDate?: string | null
  fromDriverId?: string | null
  fromVehicleId?: string | null
  fromScheduledDate?: string | null
  reason: string
  previousExceptionId?: string | null
}

export const reassign = async (deliveryNoteId: string, input: IReassignInput) => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/reassign`, input as any)
}

// Spec §2.5 detail-panel — Cancel + Manual complete actions.

export const cancelDelivery = async (deliveryNoteId: string, reason: string) => {
  return httpRequest.post(`/ord/delivery_management/${deliveryNoteId}/cancel`, { reason })
}

export const manualComplete = async (deliveryNoteId: string, reason: string, photoUrls?: string[]) => {
  return httpRequest.post(`/ord/delivery_management/${deliveryNoteId}/manual-complete`, { reason, photoUrls })
}

// §2.5 Tab 4 — public tracking link. BE mints a stateless HMAC token; the FE
// composes the shareable URL from its own origin (which proxies /ord/public).
export interface IPublicTrackLink {
  token: string
  path: string
  url: string
}

export const getPublicTrackLink = async (deliveryNoteId: string): Promise<IPublicTrackLink> => {
  return httpRequest.get(`/ord/delivery-note/${deliveryNoteId}/public-track-link`)
}

// Spec §2.5 detail-panel — POD photo archive.

export interface IDeliveryProof {
  deliveryNoteId: string
  businessId: string
  capturedByUserId: string
  capturedAt: string
  receiverName: string
  receiverTitle: string | null
  receiverPhone: string | null
  receiverEmail: string | null
  photoUrls: string[]
  signatureDataUrl: string | null
  packageCondition: string
  isPartialDelivery: boolean
  deliveredQuantity: number | null
  codReceiptPhotoUrl: string | null
  // POD review lifecycle — set by capture (PENDING/REQUIRES_REVIEW) and by the
  // coordinator verify/reject actions below. GET returns the full model.
  verificationStatus: "PENDING" | "REQUIRES_REVIEW" | "VERIFIED" | "REJECTED"
  verifiedAt: string | null
  verifiedByUserId: string | null
  rejectReason: string | null
}

export const getProof = async (deliveryNoteId: string): Promise<IDeliveryProof | null> => {
  try {
    return await httpRequest.get(`/ord/delivery-note/${deliveryNoteId}/proof`)
  } catch (err: any) {
    // 404 / NotfoundError when no proof has been captured yet — surface as null.
    if (err?.status === 404 || err?.code === "INVALID_ACTION") return null
    throw err
  }
}

// §2.5 detail-panel — coordinator POD review. Both require ORD_DN_MNG and
// return the updated DeliveryProof. verify is rejected by BE if already
// REJECTED; reject is rejected if already VERIFIED — the UI gates the buttons
// to PENDING/REQUIRES_REVIEW so those guards aren't normally hit.
export const verifyProof = async (deliveryNoteId: string): Promise<IDeliveryProof> => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/proof/verify`)
}

export const rejectProof = async (deliveryNoteId: string, reason: string): Promise<IDeliveryProof> => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/proof/reject`, { reason })
}

export const regeneratePdf = async (deliveryNoteId: string): Promise<{ url: string }> => {
  return httpRequest.post(`/ord/delivery_management/${deliveryNoteId}/regenerate-pdf`)
}

// Phase A5+C1 sidecar (dn_classification) endpoints

export interface IDnClassification {
  deliveryNoteId: string
  businessId: string
  sourceWarehouseId: string | null
  deliveryChannel: string | null
  totalSizeValue: string | null     // DECIMAL → string from sequelize
  classificationRuleId: string | null
  manuallyOverridden: boolean
  overrideReason: string | null
  overrideUserId: string | null
  classifiedAt: string | null
  createdAt?: string
  updatedAt?: string
}

export interface IClassifyItem {
  sizeNumericValue: number
  quantity: number
}

export interface IClassifyResult {
  classification: IDnClassification
  ruleId: string
  channel: string
  totalSize: number
}

export const getClassification = async (deliveryNoteId: string): Promise<IDnClassification> => {
  return httpRequest.get(`/ord/delivery-note/${deliveryNoteId}/classification`)
}

export const classify = async (
  deliveryNoteId: string,
  body: { items: IClassifyItem[]; sourceWarehouseId?: string | null },
): Promise<IClassifyResult> => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/classify`, body as any)
}

export const overrideChannel = async (
  deliveryNoteId: string,
  body: { newChannel: string; reason: string },
): Promise<IDnClassification> => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/override-channel`, body)
}

export const changeSourceWarehouse = async (
  deliveryNoteId: string,
  body: { newSourceWarehouseId: string | null },
): Promise<IDnClassification> => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/source-warehouse`, body)
}
